import {
  US_CUPS,
  UK_CUPS,
  EU_CUPS,
  EU_BAND_STEP,
} from "@/data/braSizing";

export type Unit = "cm" | "in";

export interface BraInput {
  underbust: number; // value in chosen unit
  bust: number; // value in chosen unit
  unit: Unit;
}

export interface SizingResult {
  // Inputs normalised
  underbustCm: number;
  bustCm: number;
  underbustIn: number;
  bustIn: number;

  // US
  usBand: number; // inches
  usCup: string;
  usSize: string; // "34C"

  // UK
  ukBand: number;
  ukCup: string;
  ukSize: string;

  // EU
  euBand: number;
  euCup: string;
  euSize: string;

  // Diagnostics
  diffIn: number;
  diffCm: number;

  // Sister sizes for US (same cup volume)
  sisterSizes: { system: "US"; size: string }[];

  // Cross conversion table (a few neighbouring sizes)
  conversionTable: Array<{ us: string; uk: string; eu: string }>;
}

const CM_PER_IN = 2.54;

const cmToIn = (cm: number) => cm / CM_PER_IN;
const inToCm = (inches: number) => inches * CM_PER_IN;

export function validate(i: Partial<BraInput>): { ok: boolean; error?: string } {
  if (!i.underbust || !i.bust) return { ok: false, error: "Enter both measurements" };
  if (!Number.isFinite(i.underbust) || !Number.isFinite(i.bust)) return { ok: false, error: "Invalid number" };
  if (i.bust <= i.underbust) return { ok: false, error: "Bust must be larger than underbust" };
  // Reasonable bounds (cm or in)
  const max = i.unit === "in" ? 80 : 200;
  const min = i.unit === "in" ? 18 : 45;
  for (const v of [i.underbust, i.bust]) {
    if (v < min || v > max) return { ok: false, error: "Measurement out of range" };
  }
  return { ok: true };
}

// US "snug" method: round underbust inches to nearest even number.
// Some older "+4" methods inflate band; we use the modern snug method
// recommended by most professional fitters (no addition).
function usBandFromUnderbust(underbustIn: number): number {
  const rounded = Math.round(underbustIn);
  return rounded % 2 === 0 ? rounded : rounded + 1;
}

function cupFromDiffInches(diffIn: number, ladder: string[]): string {
  const idx = Math.max(0, Math.min(ladder.length - 1, Math.round(diffIn)));
  return ladder[idx];
}

function euCupFromDiffCm(diffCm: number): string {
  // Floor-style: every full 2 cm of difference is one cup up.
  const idx = Math.max(0, Math.min(EU_CUPS.length - 1, Math.floor(diffCm / 2)));
  return EU_CUPS[idx];
}

function euBandFromUnderbustCm(underbustCm: number): number {
  return Math.round(underbustCm / EU_BAND_STEP) * EU_BAND_STEP;
}

export function computeBraSize(input: BraInput): SizingResult {
  const underbustCm = input.unit === "cm" ? input.underbust : inToCm(input.underbust);
  const bustCm = input.unit === "cm" ? input.bust : inToCm(input.bust);
  const underbustIn = cmToIn(underbustCm);
  const bustIn = cmToIn(bustCm);

  const usBand = usBandFromUnderbust(underbustIn);
  const ukBand = usBand; // US and UK band numbers match
  const euBand = euBandFromUnderbustCm(underbustCm);

  // Cup difference is bust − band (in inches for US/UK)
  const diffIn = bustIn - usBand;
  // EU uses cm diff against EU band (which is in cm)
  const diffCm = bustCm - euBand;

  const usCup = cupFromDiffInches(diffIn, US_CUPS);
  const ukCup = cupFromDiffInches(diffIn, UK_CUPS);
  const euCup = euCupFromDiffCm(diffCm);

  const usSize = `${usBand}${usCup}`;
  const ukSize = `${ukBand}${ukCup}`;
  const euSize = `${euBand}${euCup}`;

  // Sister sizes (US): same cup volume, ±2 band with ±1 cup offset
  const cupIdx = US_CUPS.indexOf(usCup);
  const sisterSizes: { system: "US"; size: string }[] = [];
  for (const dBand of [-2, 2]) {
    const newBand = usBand + dBand;
    const newCupIdx = cupIdx - dBand / 2; // smaller band → bigger cup
    if (newBand >= 28 && newBand <= 48 && newCupIdx >= 0 && newCupIdx < US_CUPS.length) {
      sisterSizes.push({ system: "US", size: `${newBand}${US_CUPS[newCupIdx]}` });
    }
  }

  // Conversion table — current size + a few neighbours
  const conversionTable: SizingResult["conversionTable"] = [];
  for (const dCup of [-1, 0, 1]) {
    const ci = cupIdx + dCup;
    if (ci < 0 || ci >= US_CUPS.length) continue;
    const ei = Math.max(0, Math.min(EU_CUPS.length - 1, Math.round((diffCm + dCup * CM_PER_IN) / 2)));
    conversionTable.push({
      us: `${usBand}${US_CUPS[ci]}`,
      uk: `${ukBand}${UK_CUPS[ci]}`,
      eu: `${euBand}${EU_CUPS[ei]}`,
    });
  }

  return {
    underbustCm,
    bustCm,
    underbustIn,
    bustIn,
    usBand,
    usCup,
    usSize,
    ukBand,
    ukCup,
    ukSize,
    euBand,
    euCup,
    euSize,
    diffIn,
    diffCm,
    sisterSizes,
    conversionTable,
  };
}

// Fit issue suggestion mapping
export type FitIssue = "band_tight" | "band_loose" | "cup_gaping" | "cup_spillage" | "straps_slip" | "underwire_pokes";

export interface FitSuggestion {
  issue: FitIssue;
  problem: { en: string; bn: string };
  solution: { en: string; bn: string };
}

export const FIT_SUGGESTIONS: FitSuggestion[] = [
  {
    issue: "band_tight",
    problem: { en: "Band feels too tight, leaves marks", bn: "ব্যান্ড খুব টাইট, দাগ ফেলে" },
    solution: {
      en: "Try one band size up and one cup size down (sister size up).",
      bn: "এক ব্যান্ড বড় ও এক কাপ ছোট সাইজ চেষ্টা করুন (সিস্টার সাইজ আপ)।",
    },
  },
  {
    issue: "band_loose",
    problem: { en: "Band rides up the back", bn: "ব্যান্ড পিঠে উঠে যায়" },
    solution: {
      en: "Try one band size down and one cup size up (sister size down).",
      bn: "এক ব্যান্ড ছোট ও এক কাপ বড় সাইজ চেষ্টা করুন।",
    },
  },
  {
    issue: "cup_gaping",
    problem: { en: "Cups gape or wrinkle at the top", bn: "কাপের উপরে গ্যাপ বা ভাঁজ পড়ে" },
    solution: {
      en: "Cup is too large — try one cup size down in the same band.",
      bn: "কাপ বড় — একই ব্যান্ডে এক কাপ ছোট চেষ্টা করুন।",
    },
  },
  {
    issue: "cup_spillage",
    problem: { en: "Spillage / overflow at top or sides", bn: "উপরে বা পাশে স্পিলেজ / উপচে পড়া" },
    solution: {
      en: "Cup is too small — try one cup size up in the same band.",
      bn: "কাপ ছোট — একই ব্যান্ডে এক কাপ বড় চেষ্টা করুন।",
    },
  },
  {
    issue: "straps_slip",
    problem: { en: "Straps slip off shoulders", bn: "স্ট্র্যাপ কাঁধ থেকে পড়ে যায়" },
    solution: {
      en: "Tighten straps; if still loose, the band may be too big.",
      bn: "স্ট্র্যাপ টাইট করুন; এরপরও আলগা থাকলে ব্যান্ড বড় হতে পারে।",
    },
  },
  {
    issue: "underwire_pokes",
    problem: { en: "Underwire pokes or sits on breast tissue", bn: "আন্ডারওয়্যার বুকে ঢোকে বা স্তনের উপর বসে" },
    solution: {
      en: "Cup is too small or wrong shape — try one cup up, or a different style.",
      bn: "কাপ ছোট বা ভুল আকৃতি — এক কাপ বড় বা ভিন্ন স্টাইল চেষ্টা করুন।",
    },
  },
];
